import { useCallback, useEffect, useRef, useState } from 'react';

import { getBrowserLocation, getWebSocketConstructor, type WebSocketLike } from './browser';

export type InputEventType =
  | 'input_registered'
  | 'input_unregistered'
  | 'input_updated'
  | 'input_event_reported';

export interface InputEventPayload {
  [key: string]: unknown;
}

export interface InputEvent {
  id?: string;
  inputId?: string;
  payload?: InputEventPayload;
  occurredAt?: string;
  createdAt?: string;
}

export interface InputStreamEvent {
  type: InputEventType;
  inputId: string;
  humanId?: string;
  name?: string;
  provider?: string;
  kind?: string;
  event?: InputEvent;
  timestamp: string;
}

export interface UseInputStreamOptions {
  /**
   * Filter pattern for input events (optional, omit for all inputs).
   * Supports:
   * - Single input: "button-001"
   * - Multiple inputs: "button-001,button-002"
   * - Wildcards: "button-*", "*-kitchen", "*dial*"
   */
  filter?: string;
  /** Whether to connect automatically (default: true) */
  enabled?: boolean;
  /** Callback when an input event is received */
  onEvent?: (event: InputStreamEvent) => void;
  /** Reconnection delay in ms (default: 3000) */
  reconnectDelay?: number;
}

export interface UseInputStreamResult {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Most recent event received */
  lastEvent: InputStreamEvent | null;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

/**
 * Hook to subscribe to real-time input events via WebSocket.
 *
 * @param options - Configuration options
 * @returns Connection state and controls
 */
export function useInputStream(options: UseInputStreamOptions = {}): UseInputStreamResult {
  const { filter, enabled = true, onEvent, reconnectDelay = 3000 } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<InputStreamEvent | null>(null);

  const wsRef = useRef<WebSocketLike | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const onEventRef = useRef(onEvent);

  onEventRef.current = onEvent;

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, [clearReconnectTimeout]);

  const connect = useCallback(() => {
    if (!mountedRef.current || !enabled) return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    const location = getBrowserLocation();
    const WebSocketImpl = getWebSocketConstructor();
    if (!location || !WebSocketImpl) return;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = location.host;
    const params = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    const url = `${protocol}//${host}/api/inputs/stream${params}`;

    const ws = new WebSocketImpl(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (mountedRef.current) {
        setIsConnected(true);
      }
    };

    ws.onclose = () => {
      if (mountedRef.current) {
        setIsConnected(false);
        if (enabled) {
          clearReconnectTimeout();
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        }
      }
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;

      try {
        const data = JSON.parse(event.data) as InputStreamEvent;
        setLastEvent(data);
        onEventRef.current?.(data);
      } catch {
        // Ignore malformed messages.
      }
    };
  }, [enabled, filter, reconnectDelay, clearReconnectTimeout]);

  const reconnect = useCallback(() => {
    disconnect();
    connect();
  }, [disconnect, connect]);

  useEffect(() => {
    mountedRef.current = true;

    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled, filter, connect, disconnect]);

  return {
    isConnected,
    lastEvent,
    reconnect,
    disconnect,
  };
}
